import type { CancelTokenSource } from 'axios';
import {
  useCallback, useEffect, useMemo, useRef, useState, useTransition,
} from 'react';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import { convertMultipolygonToWktFormat } from '~/_shared/utils/latLng/latLng.helpers';
import {
  type AgsReportPolygonResponse, type AgsReportRingDataResponse, getAgsPolygonReportData, getAgsReportRingData,
} from '~/exportReport/exportReport.repository';
import {
  createAgsReportDataRingRequest, getReportDemographicDataFromResults, getReportDemographicLabelFromResults,
  type ReportDemographicData,
} from '~/exportReport/exportReportData.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import type { ReportTableColumn } from './table/ReportTable.model';

type UseExportReportDataParams = {
  isPreviewMode?: boolean;
  template: ReportTemplate | null;
  tableColumns: ReadonlyArray<ReportTableColumn>;
};

export const useExportReportData = ({ isPreviewMode = false, template, tableColumns }: UseExportReportDataParams) => {
  const [data, setData] = useState<AgsReportRingDataResponse | AgsReportPolygonResponse | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, startTransition] = useTransition();
  const [reportPolygon, setReportPolygon] = useState<MultiPolygon | null>(null);
  const exportReportState = useToolsStateExportReportSelector();
  const cancelToken = useRef<null | CancelTokenSource>(null);

  const spreadsheetData = useLatLngSpreadsheetData();

  const center = useMemo(() => {
    if (!exportReportState.isActive) {
      return null;
    }

    if (exportReportState.params.type === ExportReportType.Location) {
      const rowData = spreadsheetData.getRow(exportReportState.params.data.markerId);
      return rowData ?? null;
    }
    if (exportReportState.params.type === ExportReportType.Radius) {
      return exportReportState.params.data.center;
    }

    return null;
  }, [exportReportState, spreadsheetData]);

  // set report polygon based on Boundary or Drive Time Polygon
  useEffect(() => {
    setReportPolygon(null);

    if (!exportReportState.isActive) {
      return;
    }

    if (exportReportState.params.type === ExportReportType.Polygon || exportReportState.params.type === ExportReportType.DriveTimePolygon) {
      setReportPolygon(exportReportState.params.data.paths);
      return;
    }
  }, [exportReportState.isActive, exportReportState.params]);

  const getDemographicData = useCallback((columnId: string, demographicId: string): ReportDemographicData | null => {
    if (isPreviewMode) {
      return null;
    }

    if (isLoading) {
      return {
        id: 'empty-value',
        value: '',
        label: '',
      };
    }

    return getReportDemographicDataFromResults(data?.areas ?? [], columnId, demographicId);
  }, [data?.areas, isLoading, isPreviewMode]);

  const getDemographicLabel = useCallback((demographicId: string): string | null => {
    if (isLoading) {
      return '';
    }

    return getReportDemographicLabelFromResults(data?.areas ?? [], demographicId);
  }, [data?.areas, isLoading]);

  // Location/Radius
  useEffect(() => {
    setIsError(false);

    if (!template || !center || isPreviewMode) {
      return;
    }

    const reportDataRequest = createAgsReportDataRingRequest({
      center,
      tableColumns,
      varlistId: template.varlistId,
      country: 'usa',
    });

    startTransition(async () => {
      try {
        if (reportDataRequest.radiiInMiles.length) {
          cancelToken.current = createCancelToken();
          const results = await getAgsReportRingData(reportDataRequest, cancelToken.current.token);

          setData(results.report);
        }
      }
      catch {
        setIsError(true);
      }
    });
  }, [center, isPreviewMode, template, tableColumns]);

  // Polygon: Boundary/Drive Time Polygon
  useEffect(() => {
    setIsError(false);

    if (!template || !reportPolygon || isPreviewMode) {
      return;
    }

    startTransition(async () => {
      try {
        const polygon = convertMultipolygonToWktFormat(reportPolygon);

        cancelToken.current = createCancelToken();
        const results = await getAgsPolygonReportData({
          country: 'usa',
          varlistId: template.varlistId,
          polygon,
        }, cancelToken.current.token);

        setData(results.report);
      }
      catch {
        setIsError(true);
      }
    });
  }, [reportPolygon, template, isPreviewMode]);

  useEffect(() => {
    if (!data) {
      return;
    }

    Object.assign(window, {
      isReportFullyLoaded: true,
    });
  }, [data]);

  useEffect(() => {
    return () => {
      cancelToken.current?.cancel();
    };
  }, []);

  return {
    isLoading,
    isError,
    getDemographicData,
    getDemographicLabel,
  };
};
